import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  adminApi,
  adminAuth,
  type CustomerDetail,
  type EmailLogEntry,
  type FollowUp,
} from '../lib/adminApi';
import {
  computeCustomerDeadlines,
  formatDeadlineDate,
  formatDeadlineRelative,
  urgencyTone,
  type CompanyDeadline,
} from '../lib/chDeadlines';

type Tab = 'overview' | 'activity' | 'communications' | 'companies' | 'filings' | 'tasks';

/**
 * Single client record – one page summarising every order, company and
 * pending task for a customer (identified by email). Tabs across the top
 * so the page stays scannable.
 */
export function AdminCustomerDetail() {
  const { email: rawEmail } = useParams();
  const navigate = useNavigate();
  const email = decodeURIComponent(rawEmail || '');
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    if (!email) return;
    adminApi.getCustomer(email).then(setCustomer).catch((e) => setError(String(e.message)));
  }, [email, navigate]);

  // Fetch all pending follow-ups for any of this customer's orders.
  useEffect(() => {
    if (!customer) return;
    const orderIds = customer.orders.map((o) => o.id);
    // The API is per-order; pull each in parallel and flatten.
    Promise.all(
      orderIds.map((id) => adminApi.listFollowUps('all', id).then((r) => r.items)),
    )
      .then((arrays) => setFollowUps(arrays.flat()))
      .catch(() => {});
  }, [customer]);

  if (error) {
    return (
      <AdminLayout>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </AdminLayout>
    );
  }
  if (!customer) {
    return (
      <AdminLayout>
        <div className="text-sm text-slate-500">Loading…</div>
      </AdminLayout>
    );
  }

  const pendingTasks = followUps.filter((f) => f.status === 'PENDING').sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  return (
    <AdminLayout>
      <Link to="/admin/customers" className="text-sm text-brand-700 hover:underline">
        ← All customers
      </Link>

      {/* Header */}
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Avatar name={customer.displayName || customer.email} />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {customer.leadDisplayId && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-xs font-semibold ring-1 ring-inset ${
                    customer.lifetimePence > 0
                      ? 'bg-green-100 text-green-800 ring-green-300'
                      : 'bg-amber-100 text-amber-800 ring-amber-300'
                  }`}
                  title={customer.lifetimePence > 0 ? 'Paid customer' : 'Lead — no payment yet'}
                >
                  {customer.leadDisplayId}
                </span>
              )}
              <h1 className="text-2xl font-semibold text-slate-900">
                {customer.displayName || customer.email}
              </h1>
              {customer.archived && (
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                  Archived
                </span>
              )}
            </div>
            <p className="mt-0.5 text-sm text-slate-500">
              <a href={`mailto:${customer.email}`} className="hover:text-brand-700">
                {customer.email}
              </a>
            </p>
          </div>
        </div>
        <div className="text-center text-xs">
          <Stat label="Companies" value={String(customer.companies.length)} />
        </div>
      </div>

      <ArchiveBar
        customer={customer}
        onChange={() =>
          adminApi.getCustomer(customer.email).then(setCustomer).catch(() => {})
        }
      />

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-slate-200 text-sm">
        {(['overview', 'activity', 'communications', 'companies', 'filings', 'tasks'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`relative px-4 py-2 capitalize transition-colors ${
              tab === t
                ? 'text-brand-700 font-medium'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {t}
            {t === 'tasks' && pendingTasks.length > 0 && (
              <span className="ml-1.5 rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold text-brand-700">
                {pendingTasks.length}
              </span>
            )}
            {tab === t && (
              <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-brand-700" />
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'overview' && <Overview customer={customer} pendingTasks={pendingTasks} />}
        {tab === 'activity' && <ActivityTab customer={customer} />}
        {tab === 'communications' && <CommunicationsTab customerEmail={customer.email} />}
        {tab === 'companies' && (
          <CompaniesTab
            customer={customer}
            onChange={() =>
              adminApi.getCustomer(customer.email).then(setCustomer).catch(() => {})
            }
          />
        )}
        {tab === 'filings' && <FilingsTab customer={customer} />}
        {tab === 'tasks' && <TasksTab tasks={followUps} />}
      </div>
    </AdminLayout>
  );
}

function Overview({ customer, pendingTasks }: { customer: CustomerDetail; pendingTasks: FollowUp[] }) {
  const recentOrders = customer.orders.slice(0, 3);
  const deadlines = computeCustomerDeadlines(customer);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Recent filings" link={{ label: 'See all', to: '#' }}>
        {recentOrders.length === 0 ? (
          <p className="text-sm text-slate-500">No filings yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recentOrders.map((o) => (
              <li key={o.id} className="py-3">
                <Link to={`/admin/orders/${o.id}`} className="block hover:opacity-90">
                  <div className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-900">{o.companyName}</div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {o.companyNumber} · Period end {o.periodEnd}
                      </div>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Companies House deadlines">
        {deadlines.length === 0 ? (
          <p className="text-sm text-slate-500">
            No projected deadlines yet. Deadlines appear here once we've filed
            at least one accepted return for this customer.
          </p>
        ) : (
          <ul className="space-y-3">
            {deadlines.map((d) => (
              <DeadlineRow key={d.companyNumber} d={d} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Pending tasks">
        {pendingTasks.length === 0 ? (
          <p className="text-sm text-slate-500">No pending tasks for this customer.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {pendingTasks.slice(0, 5).map((t) => (
              <li key={t.id} className="py-3 text-sm">
                <div className="font-medium text-slate-900">{t.companyName}</div>
                <p className="mt-0.5 text-xs text-slate-600">{t.reason}</p>
                <div className="mt-1 text-[11px] text-slate-500">Due {t.dueDate}</div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function DeadlineRow({ d }: { d: CompanyDeadline }) {
  return (
    <li className={`rounded-lg p-3 ring-1 ring-inset ${urgencyTone(d.urgency)}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{d.companyName}</div>
          <div className="text-[11px] opacity-80">{d.companyNumber}</div>
        </div>
        <div className="text-right text-[11px] font-bold uppercase tracking-wide">
          {formatDeadlineRelative(d.daysUntilDeadline)}
        </div>
      </div>
      <div className="mt-2 text-xs">
        <span className="opacity-70">Accounts due</span>{' '}
        <span className="font-semibold">{formatDeadlineDate(d.filingDeadline)}</span>
      </div>
      <div className="mt-0.5 text-[11px] opacity-70">
        For the period ending {formatDeadlineDate(d.nextPeriodEnd)}
      </div>
    </li>
  );
}

function CompaniesTab({
  customer,
  onChange,
}: {
  customer: CustomerDetail;
  onChange: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Company</th>
            <th className="px-4 py-3 text-right">Filings</th>
            <th className="px-4 py-3 text-right">Lifetime</th>
            <th className="px-4 py-3 text-right">Last filed</th>
            <th className="px-4 py-3 text-right"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {customer.companies.map((c) => (
            <tr key={c.companyNumber} className={`hover:bg-slate-50 ${c.archived ? 'opacity-60' : ''}`}>
              <td className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  {c.displayId && (
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 font-mono text-[10px] font-semibold ring-1 ring-inset ${
                        c.lifetimePence > 0
                          ? 'bg-green-100 text-green-800 ring-green-300'
                          : 'bg-amber-100 text-amber-800 ring-amber-300'
                      }`}
                      title={c.lifetimePence > 0 ? 'Paid — this company has revenue' : 'No payment yet for this company'}
                    >
                      {c.displayId}
                    </span>
                  )}
                  <span className="font-medium text-slate-900">{c.companyName}</span>
                  {c.archived && (
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{c.companyNumber}</div>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{c.filings}</td>
              <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                £{(c.lifetimePence / 100).toFixed(2)}
              </td>
              <td className="px-4 py-3 text-right text-xs text-slate-500">
                {c.lastFiledAt ? new Date(c.lastFiledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'}
              </td>
              <td className="px-4 py-3 text-right">
                <CompanyArchiveButton
                  email={customer.email}
                  companyNumber={c.companyNumber}
                  archived={c.archived}
                  onChange={onChange}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyArchiveButton({
  email,
  companyNumber,
  archived,
  onChange,
}: {
  email: string;
  companyNumber: string;
  archived: boolean;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (archived) {
      setBusy(true);
      try {
        await adminApi.unarchiveCustomer(email, { companyNumber });
        onChange();
      } finally {
        setBusy(false);
      }
      return;
    }
    const reason = prompt(
      `Mark company ${companyNumber} as inactive for this customer?\n\n` +
      `Reason (optional):`,
    );
    if (reason === null) return; // cancelled
    setBusy(true);
    try {
      await adminApi.archiveCustomer(email, { companyNumber, reason: reason || undefined });
      onChange();
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className="text-xs text-slate-400 hover:text-slate-700"
    >
      {busy ? '…' : archived ? 'Reactivate' : 'Mark inactive'}
    </button>
  );
}

/**
 * Whole-customer archive bar — appears above the tabs. Visible only when
 * the customer is archived (then it's a "currently archived" banner) or
 * always actionable from the tab footer? Simpler: only show when active
 * to offer 'Archive customer', and as a banner when archived.
 */
function ArchiveBar({
  customer,
  onChange,
}: {
  customer: CustomerDetail;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function archive() {
    const reason = prompt(
      `Mark this whole customer as inactive?\n\n` +
      `They will no longer appear in the active customer list. Existing ` +
      `data is kept — you can reactivate at any time.\n\n` +
      `Reason (optional):`,
    );
    if (reason === null) return;
    setBusy(true);
    try {
      await adminApi.archiveCustomer(customer.email, { reason: reason || undefined });
      onChange();
    } finally {
      setBusy(false);
    }
  }
  async function unarchive() {
    setBusy(true);
    try {
      await adminApi.unarchiveCustomer(customer.email);
      onChange();
    } finally {
      setBusy(false);
    }
  }
  if (customer.archived) {
    return (
      <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-100 px-4 py-2 text-sm">
        <div className="text-slate-700">
          <strong>Archived</strong>
          {customer.archivedAt && (
            <span className="ml-2 text-xs text-slate-500">
              · since{' '}
              {new Date(customer.archivedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </span>
          )}
          {customer.archivedReason && (
            <span className="ml-2 text-xs text-slate-500">· {customer.archivedReason}</span>
          )}
        </div>
        <button
          onClick={unarchive}
          disabled={busy}
          className="rounded-md bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
        >
          {busy ? '…' : 'Reactivate customer'}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-4 flex justify-end">
      <button
        onClick={archive}
        disabled={busy}
        className="text-xs text-slate-400 hover:text-slate-700"
      >
        {busy ? '…' : 'Mark customer inactive'}
      </button>
    </div>
  );
}

function FilingsTab({ customer }: { customer: CustomerDetail }) {
  return (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Order</th>
            <th className="px-4 py-3">Company</th>
            <th className="px-4 py-3">Period</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {customer.orders.map((o) => (
            <tr key={o.id} className="hover:bg-slate-50">
              <td className="px-4 py-3">
                <Link to={`/admin/orders/${o.id}`} className="font-mono text-xs text-brand-700 hover:underline">
                  {o.id.replace(/^ord_/, '').slice(0, 10)}…
                </Link>
              </td>
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{o.companyName}</div>
                <div className="text-xs text-slate-500">{o.companyNumber}</div>
              </td>
              <td className="px-4 py-3 text-xs text-slate-600">
                {o.periodStart} → {o.periodEnd}
              </td>
              <td className="px-4 py-3"><StatusPill status={o.status} /></td>
              <td className="px-4 py-3 text-right tabular-nums">£{(o.amountPence / 100).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Activity timeline — the "History" tab from the Debitam mock. Synthesises
 * a chronological log of everything that's happened with this customer
 * from existing order fields (no extra backend call). Each order
 * contributes 1-4 events: created, paid, submitted, accepted/rejected.
 */
type ActivityEvent = {
  ts: string;
  type: 'created' | 'paid' | 'submitting' | 'accepted' | 'rejected';
  orderId: string;
  companyName: string;
  companyNumber: string;
  detail?: string;
  amountPence?: number;
};

function buildActivity(customer: CustomerDetail): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  for (const o of customer.orders) {
    events.push({
      ts: o.createdAt,
      type: 'created',
      orderId: o.id,
      companyName: o.companyName,
      companyNumber: o.companyNumber,
      amountPence: o.amountPence,
    });
    if (o.paidAt) {
      events.push({
        ts: o.paidAt,
        type: 'paid',
        orderId: o.id,
        companyName: o.companyName,
        companyNumber: o.companyNumber,
        amountPence: o.amountPence,
      });
    }
    if (o.submittedAt && (o.status === 'ACCEPTED' || o.status === 'REJECTED')) {
      // Approximate "submitting" as a moment before the final outcome —
      // we don't track submittingAt separately, so reuse submittedAt.
      events.push({
        ts: o.submittedAt,
        type: o.status === 'ACCEPTED' ? 'accepted' : 'rejected',
        orderId: o.id,
        companyName: o.companyName,
        companyNumber: o.companyNumber,
        detail: o.status === 'ACCEPTED'
          ? (o.chAckNumber ? `CH ack ${o.chAckNumber}` : 'Filed with Companies House')
          : (o.errorReason || 'Rejected by Companies House'),
      });
    } else if (o.submittedAt) {
      events.push({
        ts: o.submittedAt,
        type: 'submitting',
        orderId: o.id,
        companyName: o.companyName,
        companyNumber: o.companyNumber,
        detail: 'Submitting to Companies House',
      });
    }
  }
  // Newest first — matches the Debitam History tab where last action is on top.
  events.sort((a, b) => b.ts.localeCompare(a.ts));
  return events;
}

function ActivityTab({ customer }: { customer: CustomerDetail }) {
  const events = buildActivity(customer);
  if (events.length === 0) {
    return <p className="text-sm text-slate-500">No activity recorded yet.</p>;
  }
  return (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
      <ul className="divide-y divide-slate-100">
        {events.map((e, i) => (
          <li key={`${e.orderId}-${e.type}-${i}`} className="flex items-start gap-3 px-5 py-4">
            <ActivityIcon type={e.type} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-sm font-medium text-slate-900">
                    {ACTIVITY_LABEL[e.type]}
                  </span>
                  <span className="ml-2 text-xs text-slate-500">
                    <Link to={`/admin/orders/${e.orderId}`} className="hover:text-brand-700">
                      {e.companyName} ({e.companyNumber})
                    </Link>
                  </span>
                </div>
                <time className="text-xs text-slate-500" dateTime={e.ts}>
                  {new Date(e.ts).toLocaleString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </time>
              </div>
              {e.detail && (
                <p className="mt-0.5 text-xs text-slate-600">{e.detail}</p>
              )}
              {e.amountPence != null && (e.type === 'paid' || e.type === 'created') && (
                <p className="mt-0.5 text-xs text-slate-500">
                  Amount: <span className="font-medium text-slate-700">£{(e.amountPence / 100).toFixed(2)}</span>
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const ACTIVITY_LABEL: Record<ActivityEvent['type'], string> = {
  created: 'Order created',
  paid: 'Payment received',
  submitting: 'Submitting to Companies House',
  accepted: 'Accepted by Companies House',
  rejected: 'Rejected by Companies House',
};

function ActivityIcon({ type }: { type: ActivityEvent['type'] }) {
  const tone: Record<ActivityEvent['type'], string> = {
    created: 'bg-slate-100 text-slate-600',
    paid: 'bg-blue-100 text-blue-700',
    submitting: 'bg-indigo-100 text-indigo-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const icon: Record<ActivityEvent['type'], string> = {
    created: '+',
    paid: '£',
    submitting: '↗',
    accepted: '✓',
    rejected: '✗',
  };
  return (
    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${tone[type]}`}>
      {icon[type]}
    </div>
  );
}

/**
 * Communications timeline — every email we've sent to or about this
 * customer, pulled from the email_log table. Distinguishes customer-
 * facing receipts from admin-internal alerts so the operator can scan
 * the right rows quickly.
 */
function CommunicationsTab({ customerEmail }: { customerEmail: string }) {
  const [emails, setEmails] = useState<EmailLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'customer' | 'admin'>('all');

  useEffect(() => {
    setEmails(null);
    setError(null);
    adminApi
      .listEmails({ customerEmail })
      .then((r) => setEmails(r.items))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load emails'));
  }, [customerEmail]);

  if (error) {
    return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  }
  if (emails === null) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (emails.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
        <p className="text-sm text-slate-500">
          No emails logged yet for this customer.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          New emails sent from this point on will be logged here.
        </p>
      </div>
    );
  }

  const filtered = emails.filter((e) =>
    filter === 'all'
      ? true
      : filter === 'customer'
      ? e.direction === 'OUTBOUND_CUSTOMER'
      : e.direction === 'OUTBOUND_ADMIN',
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-xs">
        <FilterPill
          label={`All (${emails.length})`}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterPill
          label={`To customer (${emails.filter((e) => e.direction === 'OUTBOUND_CUSTOMER').length})`}
          active={filter === 'customer'}
          onClick={() => setFilter('customer')}
        />
        <FilterPill
          label={`To admin (${emails.filter((e) => e.direction === 'OUTBOUND_ADMIN').length})`}
          active={filter === 'admin'}
          onClick={() => setFilter('admin')}
        />
      </div>

      <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <ul className="divide-y divide-slate-100">
          {filtered.map((e) => (
            <li key={e.id} className="flex items-start gap-3 px-5 py-4">
              <EmailIcon direction={e.direction} status={e.status} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium text-slate-900">{e.subject}</span>
                  </div>
                  <time className="text-xs text-slate-500" dateTime={e.sentAt}>
                    {new Date(e.sentAt).toLocaleString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </time>
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  <span className="font-mono">{e.template}</span>
                  <span className="mx-1.5 text-slate-300">·</span>
                  to <span className="text-slate-700">{e.toAddress}</span>
                  {e.orderId && (
                    <>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <Link to={`/admin/orders/${e.orderId}`} className="text-brand-700 hover:underline">
                        order
                      </Link>
                    </>
                  )}
                </div>
                {e.status === 'FAILED' && e.errorMessage && (
                  <div className="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">
                    {e.errorMessage}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 font-medium ring-1 ring-inset transition-colors ${
        active
          ? 'bg-brand-50 text-brand-700 ring-brand-200'
          : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}

function EmailIcon({
  direction,
  status,
}: { direction: 'OUTBOUND_CUSTOMER' | 'OUTBOUND_ADMIN'; status: string }) {
  const tone =
    status === 'FAILED'
      ? 'bg-red-100 text-red-700'
      : direction === 'OUTBOUND_CUSTOMER'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-slate-200 text-slate-700';
  return (
    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${tone}`}>
      {status === 'FAILED' ? '!' : direction === 'OUTBOUND_CUSTOMER' ? '✉' : '🔒'}
    </div>
  );
}

function TasksTab({ tasks }: { tasks: FollowUp[] }) {
  const pending = tasks.filter((t) => t.status === 'PENDING').sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const completed = tasks.filter((t) => t.status !== 'PENDING').sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));
  if (tasks.length === 0) {
    return <p className="text-sm text-slate-500">No follow-ups yet.</p>;
  }
  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <Panel title="Pending">
          <ul className="divide-y divide-slate-100">
            {pending.map((t) => (
              <li key={t.id} className="py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/orders/${t.orderId}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {t.companyName}
                    </Link>
                    <p className="mt-0.5 text-xs text-slate-600">{t.reason}</p>
                  </div>
                  <span className="text-[11px] text-slate-500">{t.dueDate}</span>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {completed.length > 0 && (
        <Panel title="History">
          <ul className="divide-y divide-slate-100">
            {completed.map((t) => (
              <li key={t.id} className="py-3 text-sm text-slate-500">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="text-slate-700">{t.companyName}</span> · {t.reason}
                    {t.completedNote && <div className="mt-0.5 text-xs italic">"{t.completedNote}"</div>}
                  </div>
                  <span className="text-[11px]">{t.completedAt ? new Date(t.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

// ─── building blocks ─────────────────────────────────────────────────────

function Panel({ title, link, children }: { title: string; link?: { label: string; to: string }; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {link && <Link to={link.to} className="text-xs text-brand-700 hover:underline">{link.label}</Link>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-white px-3 py-2 ring-1 ring-slate-200">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('');
  return (
    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-lg font-bold text-brand-700 ring-2 ring-brand-200">
      {initials || '👤'}
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  PENDING_PAYMENT: 'bg-amber-50 text-amber-700 ring-amber-200',
  PAID: 'bg-blue-50 text-blue-700 ring-blue-200',
  SUBMITTING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ACCEPTED: 'bg-green-50 text-green-700 ring-green-200',
  REJECTED: 'bg-red-50 text-red-700 ring-red-200',
};

function StatusPill({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}
